import React, { useEffect, useRef, useState } from "react";
import MenuTooltip from "./MenuTooltip";

/**
 * Renders the scanned menu image with interactive hotspots over each
 * detected dish. Hover or tap a hotspot to surface the translation in
 * MenuTooltip.
 *
 * Bounding boxes (`bbox`) are normalised 0..1000 in `[ymin, xmin, ymax, xmax]`
 * order — the format Gemini returns.
 */
export default function MenuOverlay({
  imgUrl,
  items,
  activeIdx,
  onHover,
  onPin,
  onSpeak,
}) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    if (el.complete) update();
    el.addEventListener("load", update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("load", update);
      ro.disconnect();
    };
  }, [imgUrl]);

  // Click outside any box → un-pin
  function handleContainerClick(e) {
    if (e.target === containerRef.current || e.target === imgRef.current) {
      onPin(null);
    }
  }

  return (
    <div
      ref={containerRef}
      onClick={handleContainerClick}
      className="relative bg-cream border-2 border-ink rounded-sm overflow-hidden select-none"
      data-testid="menu-overlay"
    >
      <img
        ref={imgRef}
        src={imgUrl}
        alt="menu"
        className="block w-full h-auto"
        draggable={false}
      />
      {size.w > 0 &&
        items.map((it, idx) => {
          const [ymin, xmin, ymax, xmax] = it.bbox;
          const top = (ymin / 1000) * 100;
          const left = (xmin / 1000) * 100;
          const height = ((ymax - ymin) / 1000) * 100;
          const width = ((xmax - xmin) / 1000) * 100;
          const isActive = activeIdx === idx;
          return (
            <button
              key={`hs-${idx}-${it.original}`}
              type="button"
              data-testid={`menu-hotspot-${idx}`}
              onMouseEnter={() => onHover(idx)}
              onMouseLeave={() => onHover(null)}
              onClick={(e) => {
                e.stopPropagation();
                onPin(activeIdx === idx ? null : idx);
              }}
              aria-label={it.translation}
              style={{
                top: `${top}%`,
                left: `${left}%`,
                height: `${Math.max(height, 2.4)}%`,
                width: `${Math.max(width, 8)}%`,
              }}
              className={`absolute border-2 rounded-sm transition-colors ${
                isActive
                  ? "border-terracotta bg-terracotta/20"
                  : "border-terracotta/55 bg-terracotta/5 hover:bg-terracotta/15"
              }`}
            />
          );
        })}
      {activeIdx != null && items[activeIdx] && size.w > 0 && (
        <MenuTooltip
          item={items[activeIdx]}
          containerW={size.w}
          onClose={() => onPin(null)}
          onSpeak={onSpeak}
        />
      )}
    </div>
  );
}
